import { useState, useEffect, useCallback } from 'react';
import { getImageUrl, S3ImageUtilOptions } from '../utils/s3ImageUtils';

export interface UseS3ImageOptions extends S3ImageUtilOptions {
  enabled?: boolean; // Whether to fetch the image URL automatically
}

export interface UseS3ImageResult {
  imageUrl: string;
  isLoading: boolean;
  error: string | null;
  retry: () => void;
}

/**
 * Custom hook to handle S3 image URLs with presigned URL resolution
 * @param imageKey - The S3 key or existing URL
 * @param options - Configuration options
 * @returns Object with imageUrl, loading state, error, and retry function
 */
export const useS3Image = (
  imageKey: string | null | undefined,
  options: UseS3ImageOptions = {}
): UseS3ImageResult => {
  const [imageUrl, setImageUrl] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const { enabled = true, ...s3Options } = options;

  const fetchImageUrl = useCallback(async () => {
    if (!imageKey || !enabled) {
      setImageUrl('');
      setIsLoading(false);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const url = await getImageUrl(imageKey, s3Options);
      setImageUrl(url);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load image';
      setError(errorMessage);
      console.error('useS3Image error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [imageKey, enabled, s3Options.expires]);

  const retry = useCallback(() => {
    fetchImageUrl();
  }, [fetchImageUrl]);

  useEffect(() => {
    fetchImageUrl();
  }, [fetchImageUrl]);

  return {
    imageUrl,
    isLoading,
    error,
    retry,
  };
}; 